import { useEffect } from 'react'

import { useAuth, useUser } from '@clerk/clerk-react'
import { datadogRum } from '@datadog/browser-rum'
import posthog from 'posthog-js'

import { useLocation } from '@redwoodjs/router'

import { DatadogSettings, EnvVariables } from 'src/constants'
import { useLogSentEmailActivityMutation } from 'src/fetch/sentEmailActivities'
import { usePersistentIds } from 'src/hooks/use-persistent-ids'
import { AppStateContextData } from 'src/layouts/AppStateContextLayout/types'
import { MapModeType } from 'src/types'

export enum MonitoringEvent {
  AiHotTopicClicked = 'ai_hot_topic_clicked',
  EntityVersionSearchItemSelected = 'entity_version_search_item_selected',
  EntityVersionSearchMapLaunch = 'entity_version_search_map_launch',
  OrganizationPageView = 'organization_page_view',
  PlanReviewDiffModeLaunched = 'plan_review_diff_mode_launched',
  SettlementReportDownloaded = 'settlement_report_downloaded',
  SettlementReportShared = 'settlement_report_shared',
}

export enum MonitoringContextKey {
  AiChatSessionId = 'aiChatSessionId',
  AiTopicId = 'aiTopicId',
  EntityVersionSearchMapModeType = 'entityVersionSearchMapModeType',
  IsEntityVersionMapMode = 'isEntityVersionMapMode',
  IsMultiSelect = 'isMultiSelect',
  IsProduction = 'isProduction',
  MyOrganization = 'myOrganization',
  OrganizationByPathId = 'organizationByPathId',
  OrganizationByPathSlug = 'organizationByPathSlug',
  Recipients = 'recipients',
  SettlementId = 'settlementId',
}

type MonitoringContextValue = {
  [MonitoringContextKey.AiChatSessionId]: string
  [MonitoringContextKey.AiTopicId]: string
  [MonitoringContextKey.EntityVersionSearchMapModeType]: MapModeType
  [MonitoringContextKey.IsEntityVersionMapMode]: boolean
  [MonitoringContextKey.IsMultiSelect]: boolean
  [MonitoringContextKey.IsProduction]: boolean
  [MonitoringContextKey.OrganizationByPathId]: number
  [MonitoringContextKey.OrganizationByPathSlug]: string
  [MonitoringContextKey.MyOrganization]: {
    id: number
    uuid: string
    displayName: string | null | undefined
    city: string
    slug: string
  }
  [MonitoringContextKey.Recipients]: (
    | {
        userId: number
      }
    | {
        email: string
      }
  )[]
  [MonitoringContextKey.SettlementId]: number
}

type MonitoringEventPayload = {
  [MonitoringEvent.AiHotTopicClicked]: [
    MonitoringContextKey.AiChatSessionId,
    MonitoringContextKey.AiTopicId,
  ]
  [MonitoringEvent.EntityVersionSearchItemSelected]: [
    MonitoringContextKey.IsMultiSelect,
    MonitoringContextKey.IsEntityVersionMapMode,
    MonitoringContextKey.EntityVersionSearchMapModeType,
  ]
  [MonitoringEvent.EntityVersionSearchMapLaunch]: [
    MonitoringContextKey.IsMultiSelect,
    MonitoringContextKey.EntityVersionSearchMapModeType,
  ]
  [MonitoringEvent.OrganizationPageView]: [
    MonitoringContextKey.OrganizationByPathId,
    MonitoringContextKey.OrganizationByPathSlug,
  ]
  [MonitoringEvent.PlanReviewDiffModeLaunched]: never
  [MonitoringEvent.SettlementReportDownloaded]: [MonitoringContextKey.SettlementId]
  [MonitoringEvent.SettlementReportShared]: [
    MonitoringContextKey.SettlementId,
    MonitoringContextKey.Recipients,
  ]
}

// Singleton that abstracts away the individual 3rd party providers from the rest of the app
export const monitoring = Object.freeze({
  captureEvent: <TEvent extends MonitoringEvent>(
    event: TEvent,
    ...payloadArgs: MonitoringEventPayload[TEvent] extends never
      ? []
      : [
          {
            [key in MonitoringEventPayload[TEvent][number]]: MonitoringContextValue[key]
          },
        ]
  ) => {
    posthog.capture(event, ...payloadArgs)
  },
  logError: (error: unknown, context?: Record<string, unknown>) => {
    datadogRum.addError(error, context)
  },
  setGlobalContextValue: <TKey extends MonitoringContextKey>(
    key: TKey,
    value: MonitoringContextValue[TKey]
  ) => {
    datadogRum.setGlobalContextProperty(key, value)
  },
})

export const useMonitoring = (contextData: AppStateContextData) => {
  const location = useLocation()
  const { sessionId } = usePersistentIds()
  const { mutateAsync: logSentEmailActivityMutation } = useLogSentEmailActivityMutation()

  useEffect(() => {
    // Initialize DataDog
    if (EnvVariables.DatadogRumEnabled === 'false' || !EnvVariables.DatadogClientToken) {
      return
    }
    datadogRum.init({
      applicationId: DatadogSettings.datadogApplicationId,
      clientToken: EnvVariables.DatadogClientToken,
      site: DatadogSettings.datadogSite,
      service: EnvVariables.DatadogService,
      env: EnvVariables.DatadogEnv,
      version: EnvVariables.VercelDeploymentSha,
      allowedTracingUrls: [
        // IMPORTANT!
        // We do not use https://*.govwell.com because we have Clerk proxied
        // at clerk.govwell.com, which doesn't accept the Datadog headers!
        // changing this without caution can cause an outage!
        // Check Cloudflare for any other services that could be affected by
        // changing this!
        /https:\/\/(app|dev|demo)\.govwell\.com/,
        /https:\/\/ai-chat-bot-app(\.(dev|demo))?\.govwell\.io/,
      ],
      sessionSampleRate: 100,
      sessionReplaySampleRate: 100,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'allow',
      storeContextsAcrossPages: true,
    })
  }, [])

  const { actor } = useAuth()
  const { user: clerkUser } = useUser()
  const govwellUser = contextData?.user
  const myOrg = contextData?.organization
  const isAppStateLoading = contextData?.isLoading

  const searchParams = new URLSearchParams(location.search)
  const sentEmailUuid = searchParams.get('sentEmailUuid')
  const userId = govwellUser?.id ?? null

  useEffect(() => {
    if (!sentEmailUuid || !sessionId || isAppStateLoading) {
      return
    }

    void logSentEmailActivityMutation({
      input: {
        sentEmailUuid,
        sessionId,
        userId,
      },
    })
  }, [logSentEmailActivityMutation, sessionId, sentEmailUuid, userId, isAppStateLoading])

  useEffect(() => {
    // Set user data
    const name = clerkUser?.fullName
    const email = clerkUser?.primaryEmailAddress?.emailAddress
    if (email && name) {
      const wasImpersonatedByUserWithId = actor?.sub
      const wasImpersonatedByUserWithEmail = actor?.email
      const userType = govwellUser?.type
      const userGroup = govwellUser?.group

      const commonProps = {
        id: clerkUser?.id,
        name,
        ...(wasImpersonatedByUserWithId ? { wasImpersonatedByUserWithId } : {}),
        ...(wasImpersonatedByUserWithEmail ? { wasImpersonatedByUserWithEmail } : {}),
        ...(userType ? { userType } : {}),
        ...(userGroup ? { userGroup } : {}),
      }

      datadogRum.setUser({
        ...commonProps,
        email,
      })

      // Set PostHog user
      posthog.identify(email, {
        ...commonProps,
        ...(myOrg ? { organizationId: myOrg.id, slug: myOrg.slug } : {}),
      })
    }
  }, [clerkUser, actor?.sub, actor?.email, myOrg, govwellUser])

  useEffect(() => {
    // Set my org data
    if (myOrg) {
      const myOrgContext = {
        id: myOrg.id,
        uuid: myOrg.uuid,
        displayName: myOrg.displayName,
        city: myOrg.city,
        slug: myOrg.slug,
      }

      datadogRum.setGlobalContextProperty(MonitoringContextKey.MyOrganization, myOrgContext)
    }
  }, [myOrg])

  const organizationByPath = contextData?.organizationByPath
  useEffect(() => {
    if (organizationByPath) {
      monitoring.captureEvent(MonitoringEvent.OrganizationPageView, {
        [MonitoringContextKey.OrganizationByPathId]: organizationByPath.id,
        [MonitoringContextKey.OrganizationByPathSlug]: organizationByPath.slug,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to capture the event when the org ID changes
  }, [organizationByPath?.id])
}

export const resetMonitoring = (isLogin?: boolean) => {
  datadogRum.clearUser()
  datadogRum.setGlobalContext({})

  // In order to stitch unauthenticated sessions with authenticated sessions, we don't want to reset the session when logging in
  if (!isLogin) {
    posthog.reset(true) // resets the user and session
  }
}
